import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { DeleteAccountButton } from '../components/account/DeleteAccountButton'
import { PasskeyManager } from '../components/account/PasskeyManager'
import { PlayerNameEditor } from '../components/account/PlayerNameEditor'
import { SignInOptions } from '../components/account/SignInOptions'
import { cardClass, errorClass, linkButtonClass, secondaryButtonClass } from '../components/account/styles'
import { StreakCard } from '../components/StreakCard'
import { usePlayer } from '../contexts/PlayerContext'
import { usePlayerAuth } from '../contexts/PlayerAuthContext'
import { trackPageView } from '../utils/analytics'

// Account page: sign-in options when signed out, profile + sign-out when signed
// in. There are no passwords. No game is gated behind an account; an account
// just lets a player keep their name and streak across devices (the same account
// works in the Zwizle mobile app). See docs/account.md.
export const AccountPage = () => {
  const {
    user,
    initializing,
    available,
    methods,
    passkeyAvailable,
    requestEmailCode,
    signInWithEmailCode,
    signInWithGoogle,
    signInWithPasskey,
    signOut,
    deleteAccount,
  } = usePlayerAuth()

  useEffect(() => {
    try {
      trackPageView({ page: '/account' })
    } catch (err) {
      console.error('Failed to track account page view:', err)
    }
  }, [])

  return (
    <div className="max-w-md mx-auto px-4 py-8 text-left">
      <div className={cardClass}>
        {initializing ? (
          <p role="status" className="text-center text-white/60">
            Loading...
          </p>
        ) : !available ? (
          <p data-testid="auth-unavailable" className="text-white/70">
            Sign-in is not available right now. You can keep playing as a guest.
          </p>
        ) : user ? (
          <SignedInView email={user.email} onSignOut={signOut} onDeleteAccount={deleteAccount} />
        ) : (
          <SignInOptions
            methods={methods}
            passkeyAvailable={passkeyAvailable}
            onRequestEmailCode={requestEmailCode}
            onVerifyEmailCode={signInWithEmailCode}
            onGoogleSignIn={signInWithGoogle}
            onPasskeySignIn={signInWithPasskey}
          />
        )}
      </div>

      <p className="mt-4 text-center">
        <Link to="/privacy" className={linkButtonClass}>
          Privacy Policy
        </Link>
      </p>
    </div>
  )
}

function SignedInView({
  email,
  onSignOut,
  onDeleteAccount,
}: {
  email: string | null
  onSignOut: () => Promise<void>
  onDeleteAccount: () => Promise<void>
}) {
  const [signingOut, setSigningOut] = useState(false)
  const { status, profile, rename, retry } = usePlayer()

  const handleSignOut = async () => {
    setSigningOut(true)
    try {
      await onSignOut()
    } finally {
      setSigningOut(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Account</h2>
        {email && <p className="text-sm text-white/60">{email}</p>}
      </div>

      {status === 'ready' && profile && (
        <PlayerNameEditor label={profile.label} displayName={profile.displayName} onRename={rename} />
      )}
      {status === 'syncing' && (
        <p role="status" data-testid="player-syncing" className="text-sm text-white/60">
          Loading your profile...
        </p>
      )}
      {status === 'conflict' && (
        <p role="alert" data-testid="player-conflict" className={errorClass}>
          This browser is already linked to a different Zwizle account, so your name is not shown here. Sign in with
          that account to see it.
        </p>
      )}
      {status === 'error' && (
        <div>
          <p role="alert" data-testid="player-error" className={errorClass}>
            Could not load your name. Check your connection.
          </p>
          <button type="button" data-testid="player-retry" onClick={retry} className={linkButtonClass}>
            Try again
          </button>
        </div>
      )}

      <PasskeyManager />

      <StreakCard />

      <button type="button" disabled={signingOut} onClick={() => void handleSignOut()} className={secondaryButtonClass}>
        {signingOut ? 'Signing out...' : 'Sign out'}
      </button>
      <DeleteAccountButton onDelete={onDeleteAccount} />
    </div>
  )
}
